import { UNIT, USER, USER_PREFERENCES } from "../_constants/actionConstants";
import _ from "lodash";

/**
 * @typedef {Object} MiscSessionData
 * @property {string} employeeSearchWaitingFor
 * @property {Object.<string, EmployeeDto[]>} employeeSearchHistory - normalized by searchString
 * @property {string} signatureData - base64
 */

const usersInitialState = {
  openUserDetails: {}, // Normalized by userId
  openUserLists: {},
  openUserListsV2: {},
  openUnitLists: {}, // Normalized by tabId
  openUnitDetails: {}, // Normalized by tabId
  /** @type {Object.<number, EmployeeDto>} */
  openEmployeeLists: {}, // Normalized by tabId

  /** @type {Object.<number, MiscSessionData>} */
  miscSessionData: {}, // Normalized by tabId
  userRoles: [], // Fetched when application loads
  fetching: false,
  error: null,
};

export const reducer = (state = usersInitialState, action) => {
  switch (action.type) {
    case USER.REGISTER_USER:
    case USER.EDIT_USER:
    case USER.FETCH_USER:
    case UNIT.EDIT:
    case UNIT.ADD:
    case UNIT.FETCH_DETAILS:
    case USER.FETCH_EMPLOYEE:
    case USER.FETCH_ALL_EMPLOYEES:
    case USER.FETCH_ALL_USERS:
    case USER.REFRESH_ALL_USERS_V2:
    case USER.FETCH_USER_ROLES:
    case UNIT.FETCH_ALL:
    case USER.FETCH_SIGNATURE:
    case USER.ADD_SIGNATURE:
    case USER.UPDATE_SIGNATURE:
    case USER.DELETE_SIGNATURE:
    case USER_PREFERENCES.FETCH_PROFILE_PIC:
      return { ...state, fetching: true };

    case USER.FETCH_USER_REJECTED:
    case USER.FETCH_ALL_USERS_PAGED_REJECTED_V2:
    case USER.REFRESH_ALL_USERS_V2_REJECTED:
    case UNIT.FETCH_DETAILS_REJECTED:
    case USER.FETCH_EMPLOYEE_FULFILLED:
    case USER.FETCH_EMPLOYEE_REJECTED:
    case USER.REGISTER_USER_REJECTED:
    case USER.ADD_SIGNATURE_REJECTED:
    case USER.FETCH_SIGNATURE_REJECTED:
    case USER.UPDATE_SIGNATURE_REJECTED:
    case USER.DELETE_SIGNATURE_REJECTED:
    case USER.DELETE_SIGNATURE_FULFILLED:
    case USER_PREFERENCES.FETCH_PROFILE_PIC_REJECTED:
    case USER.EDIT_USER_REJECTED:
    case USER.FETCH_ALL_EMPLOYEES_REJECTED:
    case UNIT.EDIT_REJECTED:
    case UNIT.ADD_REJECTED:
    case USER.FETCH_USER_ROLES_REJECTED:
    case USER.FETCH_ALL_USERS_REJECTED:
    case UNIT.FETCH_ALL_REJECTED:
      return { ...state, fetching: false };

    case USER.FETCH_USER_ROLES_FULFILLED:
      return { ...state, fetching: false, userRoles: action.userRoles };

    case USER.FETCH_ALL_USERS_FULFILLED:
      if (action.tabId === null) return { ...state, fetching: false };
      return {
        ...state,
        fetching: false,
        openUserLists: { ...state.openUserLists, [action.tabId]: action.payload },
      };

    case USER.REGISTER_USER_FULFILLED:
    case USER.EDIT_USER_FULFILLED: {
      const { user } = action;
      if (!user) return { ...state, fetching: false };

      if (!(user.userId in state.openUserDetails)) return { ...state, fetching: false };
      return {
        ...state,
        fetching: false,
        openUserDetails: {
          ...state.openUserDetails,
          [user.userId]: user,
        },
      };
    }

    case USER.CLEAR_USER_DETAILS: {
      return {
        ...state,
        openUserDetails: {},
      };
    }

    case USER.CLEAR_USER_LIST: {
      const { tabId } = action;
      if (tabId == null) return { ...state };

      let openUserLists = _.cloneDeep(state.openUserLists);
      if (tabId in openUserLists) delete openUserLists[tabId];

      let miscSessionData = _.cloneDeep(state.miscSessionData);
      if (tabId in miscSessionData) delete miscSessionData[tabId];

      return { ...state, openUserLists, miscSessionData };
    }

    case USER.CLEAR_USER_LIST_V2: {
      const { tabId } = action;
      if (tabId == null) return { ...state };

      return {
        ...state,
        openUserListsV2: {
          ...state.openUserListsV2,
          [tabId]: {
            filters: {
              ...(state.openUserListsV2[tabId]?.filters || {
                district: null,
                unitId: null,
                searchString: "",
              }),
            },
            data: [],
            isLast: true,
          },
        },
      };
    }

    case USER.CLEAR_MISC_SESSION_DATA: {
      const { tabId } = action;
      if (tabId == null) return { ...state };

      let miscSessionData = _.cloneDeep(state.miscSessionData);
      if (tabId in miscSessionData) delete miscSessionData[tabId];

      return { ...state, miscSessionData };
    }

    case USER.FETCH_ALL_USERS_PAGED_FULFILLED: {
      const { tabId, userList, resetList } = action;
      if (tabId == null) return { ...state, fetching: false };

      // resetList takes whatever is incoming as the entire userList.
      // resetList will be true when filters are cleared, etc., and will be
      // false when paging thru the data. Going to next page will fetch
      // additional data and append to existing employeeList.
      return {
        ...state,
        fetching: false,
        openUserLists: {
          ...state.openUserLists,
          [tabId]: resetList
            ? userList
            : [...(state.openUserLists[tabId] || []), ...userList],
        },
      };
    }

    case USER.FETCH_ALL_USERS_PAGED_FULFILLED_V2: {
      const { tabId, userList, isLast, searchString } = action;
      if (tabId == null) return { ...state, fetching: false };

      // If a searchString is provided, check if it matches the stored
      // searchString awaiting results. If no match, consider the results
      // stale and do not update.
      const thereIsSearchStringAwaitingResults =
        tabId in state.miscSessionData &&
        state.miscSessionData[tabId].userSearchWaitingFor &&
        state.miscSessionData[tabId].userSearchWaitingFor === searchString;

      if (searchString && !thereIsSearchStringAwaitingResults)
        return { ...state, fetching: false };

      return {
        ...state,
        fetching: false,
        openUserListsV2: {
          ...state.openUserListsV2,
          [tabId]: {
            filters: {
              ...(state.openUserListsV2[tabId]?.filters || {
                district: null,
                unitId: null,
                searchString: "",
              }),
            },
            data: [...(state.openUserListsV2[tabId]?.data || []), ...(userList || [])],
            isLast,
          },
        },
      };
    }

    case USER.REFRESH_ALL_USERS_V2_FULFILLED: {
      const { tabId, userList } = action;
      if (tabId == null) return { ...state, fetching: false };

      return {
        ...state,
        fetching: false,
        openUserListsV2: {
          ...state.openUserListsV2,
          [tabId]: {
            filters: {
              ...(state.openUserListsV2[tabId]?.filters || {
                district: null,
                unitId: null,
                searchString: "",
              }),
            },
            data: userList || [],
            isLast: state.openUserListsV2[tabId]?.isLast ?? true,
          },
        },
      };
    }

    case USER.UPDATE_LIST_FILTER: {
      const { tabId, filters } = action;
      if (tabId == null) return { ...state, fetching: false };

      return {
        ...state,
        openUserListsV2: {
          ...state.openUserListsV2,
          [tabId]: {
            ...(state.openUserListsV2[tabId] || {}),
            filters,
          },
        },
      };
    }

    case USER.FETCH_USER_FULFILLED: {
      const { userDetails } = action;
      if (!userDetails) return { ...state, fetching: false };

      return {
        ...state,
        fetching: false,
        openUserDetails: {
          ...state.openUserDetails,
          [userDetails.userId]: userDetails, // Replace existing
        },
      };
    }

    // Whenever search string is changed, it's registered in redux store as
    // 'waitingFor' to be compared against incoming results.
    case USER.SET_USER_SEARCH_WAITINGFOR: {
      const { tabId, waitingFor } = action;
      if (tabId == null) return { ...state };
      return {
        ...state,
        miscSessionData: {
          ...state.miscSessionData,
          [tabId]: {
            ...state.miscSessionData[tabId],
            userSearchWaitingFor: waitingFor,
          },
        },
      };
    }

    case USER.FETCH_USER_SUGGESTIONS_FULFILLED: {
      const { tabId, userList, searchString } = action;
      if (tabId == null) return { ...state, fetching: false };

      if (
        tabId in state.miscSessionData &&
        state.miscSessionData[tabId].userSearchWaitingFor &&
        state.miscSessionData[tabId].userSearchWaitingFor === searchString
      )
        return {
          ...state,
          fetching: false,
          openUserLists: {
            ...state.openUserLists,
            [tabId]: userList || [],
          },
        };
      else return { ...state, fetching: false };
    }

    // Signature is stored in 'miscSessionData' under the provided tabId.
    // Since signature data is large (>10kb), be sure to clear it from
    // redux store when it's no longer needed.
    case USER.ADD_SIGNATURE_FULFILLED:
    case USER.FETCH_SIGNATURE_FULFILLED:
    case USER.UPDATE_SIGNATURE_FULFILLED: {
      const { signature } = action;

      return {
        ...state,
        miscSessionData: {
          ...state.miscSessionData,
          userSignature: signature,
        },
        fetching: false,
      };
    }

    case USER.CLEAR_SIGNATURE_DATA: {
      return {
        ...state,
        miscSessionData: {
          ...state.miscSessionData,
          userSignature: null,
        },
      };
    }

    // When User Preferences tab is closed, clear all temp data stored in
    // miscSessionData[tabId]
    case USER_PREFERENCES.CLEAR_MISC_SESSION_DATA: {
      const { tabId } = action;
      if (!tabId || !(tabId in state.miscSessionData)) return;

      const msc = { ...state.miscSessionData };
      delete msc[tabId];

      msc.userSignature = null;

      return {
        ...state,
        miscSessionData: msc,
      };
    }

    case USER_PREFERENCES.DELETE_PROFILE_PIC: {
      const { tabId } = action;

      if (!state.miscSessionData[tabId]) return;

      const msdToUpdate = { ...state.miscSessionData[tabId] };
      delete msdToUpdate.profilePicFilename;
      delete msdToUpdate.profilePicRelPath;
      delete msdToUpdate.profilePicDataUrl;

      return {
        ...state,
        miscSessionData: {
          ...state.miscSessionData,
          [tabId]: msdToUpdate,
        },
        fetching: false,
      };
    }

    case USER_PREFERENCES.FETCH_PROFILE_PIC_FULFILLED: {
      const { tabId, profilePicFilename, profilePicRelPath, profilePicDataUrl } = action;

      if (!tabId) return;

      return {
        ...state,
        miscSessionData: {
          ...state.miscSessionData,
          [tabId]: {
            ...state.miscSessionData[tabId],
            profilePicFilename,
            profilePicRelPath,
            profilePicDataUrl,
          },
        },
        fetching: false,
      };
    }

    case USER.FETCH_ALL_EMPLOYEES_FULFILLED: {
      const { tabId, employeeList, resetList } = action;
      if (tabId == null) return { ...state, fetching: false };

      // resetList takes whatever is incoming as the entire employeeList.
      // resetList will be true when filters are cleared, etc., and will be
      // false when paging thru the data. Going to next page will fetch
      // additional data and append to existing employeeList.
      return {
        ...state,
        fetching: false,
        openEmployeeLists: {
          ...state.openEmployeeLists,
          [tabId]: resetList
            ? employeeList
            : [...(state.openEmployeeLists[tabId] || []), ...employeeList],
        },
      };
    }

    // Whenever search string is changed, it's registered in redux store as
    // 'waitingFor' to be compared against incoming results.
    case USER.SET_EMPLOYEE_SEARCH_WAITINGFOR: {
      const { tabId, waitingFor } = action;
      if (tabId == null) return { ...state };
      return {
        ...state,
        miscSessionData: {
          ...state.miscSessionData,
          [tabId]: {
            ...state.miscSessionData[tabId],
            employeeSearchWaitingFor: waitingFor,
          },
        },
      };
    }

    case USER.CLEAR_EMPLOYEES_LIST: {
      const { tabId } = action;
      if (tabId == null) return { ...state };

      let openEmployeeLists = { ...state.openEmployeeLists };
      delete openEmployeeLists[tabId];

      let miscSessionData = { ...state.miscSessionData };
      delete miscSessionData[tabId];

      // for (const _tabId in state.openEmployeeLists) {
      //   if (+_tabId === tabId)
      //     openEmployeeLists[_tabId] = [];
      //   else openEmployeeLists[_tabId] = { ...state.openEmployeeLists[_tabId] };
      // }

      // let miscSessionData = {};
      // for (const _tabId in state.miscSessionData) {
      //   if (+_tabId === tabId)
      //     miscSessionData[_tabId] = {
      //       ...state.miscSessionData[_tabId],
      //       employeeSearchWaitingFor: "",
      //       employeeSearchHistory: {},
      //     };
      //   else miscSessionData[_tabId] = { ...state.miscSessionData[_tabId] };
      // }

      return { ...state, openEmployeeLists, miscSessionData };
    }

    case USER.FETCH_EMPLOYEE_SUGGESTIONS_FULFILLED: {
      const { tabId, employeeList, searchString } = action;
      if (tabId == null) return { ...state, fetching: false };

      if (
        tabId in state.miscSessionData &&
        state.miscSessionData[tabId].employeeSearchWaitingFor &&
        state.miscSessionData[tabId].employeeSearchWaitingFor === searchString
      )
        return {
          ...state,
          fetching: false,
          openEmployeeLists: {
            ...state.openEmployeeLists,
            [tabId]: employeeList || [],
          },
          miscSessionData: {
            ...state.miscSessionData,
            [tabId]: {
              ...state.miscSessionData[tabId],
              employeeSearchHistory: {
                ...state.miscSessionData[tabId].employeeSearchHistory,
                [searchString]: employeeList || [],
              },
            },
          },
        };
      else return { ...state, fetching: false };
    }

    case USER.UPDATE_EMPLOYEE: {
      const { tabId, employeeId, fields } = action;
      if (tabId == null) return { ...state };
      const employeeList = (state.openEmployeeLists[tabId] || []).map((e) => ({ ...e }));
      const emp = employeeList.find((e) => e.employeeId === employeeId);
      if (!emp) return { ...state };
      for (const key in fields) {
        if (key in emp) emp[key] = fields[key];
      }
      return {
        ...state,
        openEmployeeLists: {
          ...state.openEmployeeLists,
          [tabId]: employeeList,
        },
        miscSessionData: {
          ...state.miscSessionData,
          [tabId]: {
            ...state.miscSessionData[tabId],
            employeeSearchHistory: {},
          },
        },
      };
    }

    case UNIT.ADD_FULFILLED:
    case UNIT.EDIT_FULFILLED:
    case UNIT.FETCH_ALL_FULFILLED: {
      const { tabId, unitList } = action;
      if (!tabId) return { ...state, fetching: false };
      return {
        ...state,
        openUnitLists: {
          ...state.openUnitLists,
          [tabId]: unitList,
        },
        fetching: false,
      };
    }

    case UNIT.FETCH_DETAILS_FULFILLED: {
      const { unitId, unitDetails } = action;
      if (!unitId) return { ...state, fetching: false };
      return {
        ...state,
        openUnitDetails: { ...state.openUnitDetails, [unitId]: unitDetails },
        fetching: false,
      };
    }

    case UNIT.CLEAR_DETAILS: {
      const { tabId } = action;
      if (!tabId) return { ...state };
      return { ...state, openUnitDetails: { ...state.openUnitDetails, [tabId]: null } };
    }

    case USER.CLEAR_ALL_USER_DATA:
      // Clear all but leave unit list
      return {
        ...state,
        openUserLists: {},
      };
    default:
      return state;
  }
};
